// `ColumnChooser` (design-system §3.3, PR-E4) — the column configuration's
// editor, a `Modal` on the existing primitive. i18n-free: every word arrives
// in `labels`, translated by the caller.
//
// It lists the columns PRESENT on the current page (`presentColumns`), in the
// configured order: one checkbox per configurable column with Move up and
// Move down, and the pinned lead and `actions` trail listed as fixed. A column
// whose `presence` is false for the page is not listed at all, whatever the
// stored view says: that is D-243 ruling 3's redaction fence, and the chooser
// offers exactly what the payload proves.
//
// Every edit is handed back through `mergeColumnConfig`, so the entries for
// columns this page does not show (a fenced column, or a stale key a later
// build dropped) keep their place in the configuration and are never erased
// from a view by a reader who cannot see them. Reset widths clears the widths
// of the present columns alone, for the same reason.
//
// The caller disables the control that opens this while the list loads or
// when the page has no rows, because presence cannot be judged on an empty
// page (spec 16 §4).

import {
  mergeColumnConfig,
  presentColumns,
  type ColumnConfigEntry,
  type DataTableColumn,
  type ResolvedColumn,
} from "./data-table";
import { Button } from "./button";
import { CheckboxField } from "./field";
import { Modal } from "./modal";

export interface ColumnChooserLabels {
  readonly title: string;
  readonly close: string;
  readonly done: string;
  /** The word beside a pinned column. */
  readonly fixed: string;
  readonly resetWidths: string;
  readonly moveUp: (column: string) => string;
  readonly moveDown: (column: string) => string;
}

export interface ColumnChooserProps<Row, K extends string = string> {
  readonly columns: readonly DataTableColumn<Row, K>[];
  readonly rows: readonly Row[];
  /** The table's current entries, ordered. */
  readonly value: readonly ColumnConfigEntry[];
  /** The whole configuration after an edit. */
  readonly onChange: (next: ColumnConfigEntry[]) => void;
  readonly onClose: () => void;
  readonly labels: ColumnChooserLabels;
  /** The panel class, for the caller's width. */
  readonly className?: string | undefined;
  readonly testId?: string | undefined;
}

function nameOf<Row, K extends string>(column: ResolvedColumn<Row, K>): string {
  return column.column.label ?? column.column.header;
}

/** The entry a configurable column writes: its `hidden` only when it differs
 * from the column's default, and its width when it has one. */
function entryOf<Row, K extends string>(
  column: ResolvedColumn<Row, K>,
  hidden: boolean,
  width: number | undefined,
): ColumnConfigEntry {
  const entry: ColumnConfigEntry = { key: column.key };
  if (hidden !== (column.column.defaultHidden === true)) entry.hidden = hidden;
  if (width !== undefined) entry.width = width;
  return entry;
}

export function ColumnChooser<Row, K extends string = string>({
  columns,
  rows,
  value,
  onChange,
  onClose,
  labels,
  className,
  testId,
}: ColumnChooserProps<Row, K>) {
  const present = presentColumns(columns, rows, value);
  const configurable = present.filter((column) => column.pin === undefined);

  const emit = (ordered: readonly ResolvedColumn<Row, K>[], hiddenOverride?: [string, boolean]) => {
    const edits = ordered.map((column) =>
      entryOf(
        column,
        hiddenOverride?.[0] === column.key ? hiddenOverride[1] : column.hidden,
        column.width,
      ),
    );
    onChange(mergeColumnConfig(value, edits));
  };

  const move = (index: number, by: -1 | 1) => {
    const target = index + by;
    const moved = configurable[index];
    const other = configurable[target];
    if (moved === undefined || other === undefined) return;
    const next = [...configurable];
    next[index] = other;
    next[target] = moved;
    emit(next);
  };

  const resetWidths = () => {
    const edits = present.map((column) =>
      column.pin === undefined ? entryOf(column, column.hidden, undefined) : { key: column.key },
    );
    onChange(mergeColumnConfig(value, edits));
  };

  const anyWidth = present.some((column) => column.width !== undefined);

  return (
    <Modal
      label={labels.title}
      heading={labels.title}
      closeLabel={labels.close}
      onClose={onClose}
      className={className}
      testId={testId}
    >
      <div role="list" data-testid="column-chooser-list">
        {present.map((column) => {
          const name = nameOf(column);
          if (column.pin !== undefined) {
            return (
              <div
                key={column.key}
                role="listitem"
                className="ds-toolrow"
                data-column={column.key}
                data-fixed="true"
              >
                <span>{name}</span>
                <span className="field-optional">{labels.fixed}</span>
              </div>
            );
          }
          const index = configurable.indexOf(column);
          return (
            <div key={column.key} role="listitem" className="ds-toolrow" data-column={column.key}>
              <CheckboxField
                label={name}
                checked={!column.hidden}
                onChange={(checked) => {
                  emit(configurable, [column.key, !checked]);
                }}
                data-testid={`column-chooser-toggle-${column.key}`}
              />
              <Button
                size="sm"
                aria-label={labels.moveUp(name)}
                disabled={index <= 0}
                onClick={() => {
                  move(index, -1);
                }}
              >
                ↑
              </Button>
              <Button
                size="sm"
                aria-label={labels.moveDown(name)}
                disabled={index >= configurable.length - 1}
                onClick={() => {
                  move(index, 1);
                }}
              >
                ↓
              </Button>
            </div>
          );
        })}
      </div>
      <div className="form-actions">
        <Button size="sm" disabled={!anyWidth} onClick={resetWidths}>
          {labels.resetWidths}
        </Button>
        <Button size="sm" variant="primary" onClick={onClose}>
          {labels.done}
        </Button>
      </div>
    </Modal>
  );
}
